import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DashboardIcon from "@mui/icons-material/Dashboard";
import assessment from "../../assets/images/method.png";
import exam from "../../assets/images/exam.png";
import test from "../../assets/images/test.png";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { useNavigate } from "react-router-dom";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import medal from "../../assets/images/medal.png";
import first from "../../assets/images/first.png";
import second from "../../assets/images/second.png";
import third from "../../assets/images/third.png";
import school from "../../assets/images/school.png";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableNodata } from "../../components/TableNodata";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { OverAllLoader } from "../../components/OverAllLoader";
import { TopPerformersSkeleton, TimelineSkeleton } from "./DashboardCardSkeletons";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const SuperAdminDashboard = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const currentMonth = dayjs().month(); // 0-11
  const currentYear = dayjs().year();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(+localStorage.getItem("currentpage") || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    +localStorage.getItem("rowsPerPage") || 5
  );
  const [testList, setTestList] = useState();
  const [tableData, setTableData] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(
    +localStorage.getItem("InstituteId") || ""
  );
  const [assessmentsDetail, setAssessmentsDetail] = useState([]);
  const { showSnackbar } = useSnackbar();
  const [colleges, setColleges] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const [topRank, setTopRank] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  const avatarColors = [
    "#e53935", // red
    "#8e24aa", // purple
    "#3949ab", // indigo
    "#00897b", // teal
    "#f4511e", // orange
    "#43a047", // green
    "#fdd835", // yellow
  ];

  const commonAvatarStyle = {
    bgcolor: "#e8f2ff", // light blue background
    color: "#1e88e5", // blue text/icon color
    width: 24,
    height: 24,
    fontWeight: "bold",
    border: "2px solid #bbdefb",
  };

  const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i); // Example: [2023, 2024, 2025, 2026, 2027, 2028]

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const navigateStudentsList = (instituteId, name) => {
    navigate("/students", {
      state: {
        instituteId: instituteId,
        dashboard: true,
        instituteName: name,
      },
    });
    localStorage.setItem("prevScreenDashboard", true);
  };

  const testCount = async () => {
    try {
      const res = await api.post("GetAllAssessmentsCountForSuperAdmin", {
        page: page || 0,
        size: rowsPerPage || 5,
        search_key: search || "",
      });
      if (res?.data?.status === "success") {
        setTestList(res?.data);
        setCount(res?.data?.total_institutes);
        setTableData(res?.data?.institute_information || []);
      }
    } catch (error) {
      console.log("Error : ", error);
    }
  };
  const instituteList = async () => {
    try {
      const res = await api.post("/ListInstituteNames");
      if (res?.data?.status === "success") {
        setColleges(res?.data?.data);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", "Failed to fetch institutes.");
    }
  };

  const assessmentDetails = async () => {
    setCardsLoading(true);
    try {
      const res = await api.post("TopPerformersAndAssessmentInfo", {
        institute_id:
          +localStorage.getItem("InstituteId") || selectedCollege || null,
        month:
          selectedMonth !== null && selectedMonth !== ""
            ? selectedMonth + 1
            : null,
        year: selectedYear,
      });
      if (res?.data?.status === "success") {
        setAssessmentsDetail(res?.data?.assessment_info || []);
        setTopRank(res?.data?.student_scores || []);
      }
    } catch (error) {
      console.log("Error : ", error);
    } finally {
      setCardsLoading(false);
    }
  };

  useEffect(() => {
    instituteList();
  }, []);

  useEffect(() => {
    testCount();
  }, [page, rowsPerPage, search]);

  useEffect(() => {
    assessmentDetails();
  }, [selectedMonth, selectedYear, selectedCollege]);

  // Check login status
  const isLoggedIn = localStorage.getItem("logIn") !== "true";

  // Function to wrap content with Zoom conditionally
  const ConditionalZoom = ({ children, style, key }) => {
    if (isLoggedIn) {
      return (
        <Zoom in={loaded} style={style} key={key}>
          {children}
        </Zoom>
      );
    }
    return children;
  };

  return (
    <Box>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Grid>
            {/* Header Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <DashboardIcon sx={{ color: "#1e88e5", fontSize: 32 }} />
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ color: "#0a0a0a" }}
              >
                Dashboard
              </Typography>
            </Box>

            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Overall summary of exam activities and {getLabel("STUDENT")}'s performance
              outcomes.
            </Typography>
          </Grid>
        </Box>

        {/* Cards Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          {/* Total Exams Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="total-exams-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() =>
                navigate("/questionPaperBuilder")
              }
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  No. of {getLabel("QUESTION_PAPER")}
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={test}
                    alt={`No. of ${getLabel("QUESTION_PAPER")}`}
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_assessments || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>

          {/* Self Assessments Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="self-assessments-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() =>
                navigate("/questionPaperBuilder", {
                  state: {
                    testType: "Self Assessment",
                    testTypeId: 1,
                  },
                })
              }
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  No. of Self Assessments
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={assessment}
                    alt="Self Assessments"
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_self_assessments || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>

          {/* Proctored Tests Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="proctored-tests-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() => (
                navigate("/questionPaperBuilder", {
                  state: {
                    testType: "Proctored Assessment",
                    testTypeId: 2,
                  },
                }),
                +localStorage.setItem("TabValue", 1)
              )}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  No. of Proctored Assessments
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={exam}
                    alt="Proctored Tests"
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_proctored_assessments || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>

          {/* Institutes Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="institutes-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() =>
                navigate("/institutes", {
                  state: {
                    viewInstitute: true,
                  },
                })
              }
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  No. of Institutes
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={school}
                    alt="Institutes"
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_institutes || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>
        </Box>

        {/* Institute Performance Overview Section */}
        <ConditionalZoom
          in={loaded}
          style={{ transitionDelay: "600ms" }}
          key="performance-overview-section"
        >
          <Box
            sx={{
              p: 2,
              borderRadius: "8px",
              border: "1px solid #c1c1c6ff",
              background: "#fff",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Grid
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <SchoolOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  {getLabel("INSTITUTE")} performance Overview
                </Typography>
              </Grid>

              {isLoggedIn && (
                <Grid
                  container
                  spacing={2}
                  sx={{ mb: 3, alignItems: "center" }}
                >
                  {/* Institute Dropdown */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      size="small"
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          width: 250,
                        },
                      }}
                      options={colleges}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        colleges.find(
                          (college) =>
                            college.institute_id === selectedCollege
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        const selectedId = newValue
                          ? newValue.institute_id
                          : "";
                        localStorage.setItem(
                          "Institute_Id_Filter",
                          selectedId
                        );
                        setPage(0);
                        setSelectedCollege(selectedId);
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          className="custom-search-field"
                          {...params}
                          placeholder="All Institutes"
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "12px",
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "12px",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Month Dropdown */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{ height: 40 }}>
                      <InputLabel sx={{ fontSize: "12px" }}>Month</InputLabel>
                      <Select
                        value={selectedMonth ?? ""}
                        onChange={(e) =>
                          setSelectedMonth(
                            e.target.value !== "" ? e.target.value : null
                          )
                        }
                        displayEmpty
                        input={
                          <OutlinedInput
                            label="Month"
                            startAdornment={
                              <InputAdornment position="start">
                                <CalendarMonthIcon fontSize="small" />
                              </InputAdornment>
                            }
                          />
                        }
                        sx={{
                          "& .MuiSelect-select": { fontSize: "12px" },
                          "& .MuiOutlinedInput-root": { height: 40 },
                        }}
                      >
                        <MenuItem value="">All Months</MenuItem>
                        {months.map((month, index) => (
                          <MenuItem key={index} value={index}>
                            {month}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Year Dropdown */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{ height: 40 }}>
                      <InputLabel sx={{ fontSize: "12px" }}>Year</InputLabel>
                      <Select
                        value={selectedYear ?? ""}
                        onChange={(e) =>
                          setSelectedYear(
                            e.target.value !== "" ? e.target.value : null
                          )
                        }
                        displayEmpty
                        input={
                          <OutlinedInput
                            label="Year"
                            startAdornment={
                              <InputAdornment position="start">
                                <CalendarMonthIcon fontSize="small" />
                              </InputAdornment>
                            }
                          />
                        }
                        sx={{
                          "& .MuiSelect-select": { fontSize: "12px" },
                          "& .MuiOutlinedInput-root": { height: 40 },
                        }}
                      >
                        <MenuItem value="">All Years</MenuItem>
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                width: "100%",
              }}
            >
              {/* Top Performers Card */}
              <ConditionalZoom
                in={loaded}
                style={{ transitionDelay: "800ms" }}
                key="top-performers-card-inner"
              >
                <Box sx={{ flex: 1 }}>
                  <Grid
                    container
                    direction="column"
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #c1c1c6ff",
                      background: "#fff",
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                      p: 2,
                    }}
                  >
                    {/* Header */}
                    <Grid
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 1,
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Grid
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 1,
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={medal}
                          alt="medal"
                          sx={{ width: 30, height: 30 }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Top Performers
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Scrollable List */}
                    <Grid
                      sx={{
                        overflowY: "auto",
                        maxHeight: 350,
                        minHeight: 350,
                        pr: 1,
                      }}
                    >
                      {cardsLoading ? (
                        <TopPerformersSkeleton />
                      ) : topRank.length === 0 ? (
                        <Grid
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <TableNodata />
                        </Grid>
                      ) : (
                        topRank.map((item, index) => (
                          <Grid
                            container
                            alignItems="center"
                            key={`top-performer-${index}`}
                            sx={{
                              mb: 1,
                              p: 1,
                              borderRadius: 2,
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#f5f5f5" },
                            }}
                            onClick={() =>
                              navigate("/dashboard/examOverview", {
                                state: {
                                  assessmentId: item.assessment_id,
                                  attemptId: item.attempt_id,
                                  studentName: item?.student_name,
                                },
                              })
                            }
                          >
                            {/* Avatar + Name */}
                            <Grid
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: "35%",
                              }}
                            >
                              <Avatar sx={commonAvatarStyle}>
                                {item.student_name.charAt(0)}
                              </Avatar>
                              <Typography sx={{ fontWeight: 500 }}>
                                {item.student_name}
                              </Typography>
                            </Grid>
                            <Grid sx={{ width: "50%" }}>
                              {/* Test Type */}
                              <Typography sx={{ textAlign: "left" }}>
                                {item.test_type}
                              </Typography>
                              <Grid>
                                <Typography
                                  sx={{ fontSize: "12px", color: "#1e88e5" }}
                                >
                                  {item?.category}
                                </Typography>
                              </Grid>
                            </Grid>

                            {/* Percentage + Medal */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                gap: 1,
                                width: "15%",
                              }}
                            >
                              <Typography sx={{ fontWeight: "bold" }}>
                                {item?.final_score
                                  ? Math.round(item.final_score)
                                  : 0}
                              </Typography>

                              {index === 0 && (
                                <Box
                                  component="img"
                                  src={first}
                                  alt="First place"
                                  sx={{ width: 26, height: 26 }}
                                />
                              )}
                              {index === 1 && (
                                <Box
                                  component="img"
                                  src={second}
                                  alt="Second place"
                                  sx={{ width: 26, height: 26 }}
                                />
                              )}
                              {index === 2 && (
                                <Box
                                  component="img"
                                  src={third}
                                  alt="Third place"
                                  sx={{ width: 26, height: 26 }}
                                />
                              )}
                            </Box>
                          </Grid>
                        ))
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </ConditionalZoom>

              {/* Question Paper Timeline Card */}
              <ConditionalZoom
                in={loaded}
                style={{ transitionDelay: "800ms" }}
                key="question-paper-timeline-card-inner"
              >
                <Box sx={{ flex: 1 }}>
                  <Grid
                    container
                    direction="column"
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #c1c1c6ff",
                      background: "#fff",
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                      p: 2,
                    }}
                  >
                    {/* Header */}
                    <Grid
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 1,
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                      }}
                    >
                      <Grid
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 1,
                          alignItems: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={assessment}
                          alt="upcoming"
                          sx={{ width: 30, height: 30 }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {getLabel("QUESTION_PAPER")} timeline
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Assessment Details List */}
                    <Box
                      component="ul"
                      sx={{
                        listStyle: "none",
                        pl: 0,
                        m: 0,
                        maxHeight: 350,
                        minHeight: 350,
                        overflowY: "auto",
                      }}
                    >
                      {cardsLoading ? (
                        <TimelineSkeleton />
                      ) : assessmentsDetail.length === 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <TableNodata />
                        </Box>
                      ) : (
                        assessmentsDetail.map((item, index) => (
                          <Box
                            component="li"
                            key={`assessment-${index}`}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                              mb: 1.5,
                              borderBottom: "1px solid #f2f2f2",
                              pb: 1,
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#f9fafc",
                                borderRadius: 2,
                                pl: 1,
                              },
                            }}
                            onClick={() =>
                              navigate("/questionPaperBuilder/view", {
                                state: {
                                  assessmentId: item?.assessment_id,
                                },
                              })
                            }
                          >
                            {/* Stylish Bullet */}
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                mt: 0.8,
                                borderRadius: "50%",
                                background:
                                  avatarColors[index % avatarColors.length],
                                flexShrink: 0,
                                boxShadow: "0 0 4px rgba(0,0,0,0.15)",
                              }}
                            />

                            {/* Assessment Text */}
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {"A "}
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: "bold",
                                  color: "text.primary",
                                }}
                              >
                                {item?.test_type}
                              </Box>{" "}
                              {" question paper on "}
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: "bold",
                                  color: "text.primary",
                                }}
                              >
                                {item?.category}
                              </Box>
                              {" was created by "}
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: "bold",
                                  color: "text.primary",
                                }}
                              >
                                {item?.created_by_name}
                              </Box>{" "}
                              {" from " + item?.created_by_institute + " on "}
                              <Box
                                component="span"
                                sx={{
                                  fontWeight: "bold",
                                  color: "text.primary",
                                }}
                              >
                                {dayjs(item?.created_at).format(
                                  "MMMM D, YYYY"
                                )}
                              </Box>
                              {"."}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Grid>
                </Box>
              </ConditionalZoom>
            </Box>
          </Box>
        </ConditionalZoom>

        {/* Institute Management Overview Section */}
        <ConditionalZoom
          in={loaded}
          style={{ transitionDelay: "1000ms" }}
          key="institute-management-overview"
        >
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: "8px",
              border: "1px solid #c1c1c6ff",
              background: "#fff",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
              overflowX: "auto",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <SchoolOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  {getLabel("INSTITUTE")} Management Overview
                </Typography>
              </Box>
              <TextField
                value={search}
                onChange={(e) => {
                  setPage(0);
                  setSearch(e.target.value);
                }}
                size="small"
                variant="outlined"
                placeholder={`Search ${getLabel("INSTITUTE")}...`}
                className="custom-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Table Header */}
            <Grid
              container
              sx={{
                py: 1.5,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
                color: "text.secondary",
                flexWrap: "nowrap",
                minWidth: 760,
              }}
            >
              <Grid item xs={4} sx={{ width: "30%" }}>
                {getLabel("INSTITUTE")}
              </Grid>
              <Grid item xs={2} sx={{ width: "15%" }}>
                {getLabel("INSTITUTE_ADMIN")}
              </Grid>
              <Grid item xs={2} sx={{ width: "12%" }}>
                {getLabel("TRAINER")}
              </Grid>
              <Grid item xs={2} sx={{ width: "12%" }}>
                {getLabel("STUDENT")}
              </Grid>
              <Grid item xs={2} sx={{ width: "15%" }}>
                {getLabel("QUESTION_PAPER")}
              </Grid>
              <Grid item xs={2} sx={{ width: "10%" }}>
                Status
              </Grid>
            </Grid>

            {/* Institute Data */}
            {tableData.map((institute, index) => (
              <Grid
                container
                key={`institute-${index}`}
                alignItems="center"
                sx={{
                  py: 1.5,
                  borderBottom: "1px solid #f2f2f2",
                  "&:hover": { backgroundColor: "#fafafa" },
                  flexWrap: "nowrap",
                  minWidth: 760,
                }}
              >
                {/* Institute Info */}
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "30%",
                  }}
                >
                  <Avatar sx={commonAvatarStyle}>
                    {institute?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={600}>{institute.name}</Typography>
                  </Box>
                </Grid>

                {/* Institute Admins */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "15%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate("/instituteAdmin", {
                        state: {
                          instituteId: institute?.institute_id,
                          dashboard: true,
                          institute_Name: institute?.name,
                        },
                      })
                    }
                  >
                    <GroupOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    {institute?.admin_count || "0"}
                  </Box>
                </Grid>

                {/* Trainers */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "12%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate("/trainers", {
                        state: {
                          instituteId: institute?.institute_id,
                          dashboard: true,
                          instituteName: institute?.name,
                        },
                      })
                    }
                  >
                    <GroupOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    {institute?.trainer_count || "0"}
                  </Box>
                </Grid>

                {/* Students */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "12%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigateStudentsList(
                        institute?.institute_id,
                        institute?.name
                      )
                    }
                  >
                    <GroupOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    {institute?.student_count || "0"}
                  </Box>
                </Grid>

                {/* Question Papers */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "15%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate("/questionPaperBuilder", {
                        state: {
                          institute_id: institute?.institute_id,
                          instituteName: institute?.name,
                        },
                      })
                    }
                  >
                    <DescriptionOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    <Typography fontWeight={500}>
                      {institute?.assessment_count || "0"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Status */}
                <Grid item xs={2} sx={{ width: "10%" }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      px: 2,
                      py: 0.5,
                      borderRadius: "12px",
                      fontWeight: "bold",
                      fontSize: "12px",
                      backgroundColor:
                        institute.status === "ACTIVE"
                          ? "#d4edda"
                          : institute.status === "INACTIVE"
                            ? "#f8d7da"
                            : "#f1f1f1",
                      color:
                        institute.status === "ACTIVE"
                          ? "#155724"
                          : institute.status === "INACTIVE"
                            ? "#721c24"
                            : "#333",
                      textAlign: "center",
                    }}
                  >
                    {institute.status}
                  </Box>
                </Grid>
              </Grid>
            ))}

            {tableData.length === 0 && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <TableNodata />
              </Box>
            )}

            {((page === 0 && tableData?.length > rowsPerPage - 1) ||
              page !== 0) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  {page !== 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={
                          <ArrowBackIcon
                            sx={{
                              color: page === 0 ? "#a2a2a2" : "#414651",
                            }}
                          />
                        }
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                        sx={{
                          borderRadius: "12px",
                          textTransform: "none",
                          color: page === 0 ? "#a2a2a2" : "#414651",
                          borderColor: "#d0d0d0",
                        }}
                      >
                        Previous
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ width: "110px" }} />
                  )}

                  <Pagination
                    page={page + 1}
                    onChange={(e, value) => setPage(value - 1)}
                    count={Math.ceil(count / rowsPerPage)}
                    siblingCount={3}
                    boundaryCount={3}
                    shape="rounded"
                    hidePrevButton
                    hideNextButton
                  />
                  {page + 1 !== Math.ceil(count / rowsPerPage) ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        endIcon={
                          <ArrowForwardIcon
                            sx={{
                              color: page + 1 === count ? "#a2a2a2" : "#414651",
                            }}
                          />
                        }
                        disabled={page + 1 === count}
                        onClick={() => setPage(page + 1)}
                        sx={{
                          borderRadius: "12px",
                          textTransform: "none",
                          color: page + 1 === count ? "#a2a2a2" : "#414651",
                          borderColor: "#d0d0d0",
                        }}
                      >
                        Next
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ width: "110px" }} />
                  )}
                </Box>
              )}
          </Box>
        </ConditionalZoom>
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;
